import React, { useState } from 'react';
import { RotationMenuWrapper } from 'src/pages/stowage/StowagePage.styles';
import { RotationMenu } from './RotationView.styles';
import { useStowageStore } from 'src/context/StowageContext';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { useRotationStore } from 'src/context/RotationContext';
import { RotationItemPlaceHolder } from 'src/types/ViewModel/Stowage/Rotation/RotationItemPlaceHolder';
import PortCallRotationPanel from 'src/components/stowage/tankPlanView/popUpPanels/PortCallRotationPanel';
import BerthCallRotationPanel from 'src/components/stowage/tankPlanView/popUpPanels/BerthCallRotationPanel';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { createDeleteStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import { useAuthStore } from 'src/context/AuthContext';
import { hasRotationItemBlockedTank } from 'src/utils/stowage/stowageOperation/blockedTankUtils';
import { MenuPosition } from 'src/types/ViewModel/Stowage/MenuPosition';
import OdfjellContextMenu from 'src/components/ui/odfjellContextMenu/OdfjellContextMenu';
import { getBerthCalls, isValidPortCall } from 'src/utils/stowage/stowageRotationUtils';

const ROTATION_MENU_Y_POS_OFFSET = 105;
const ROTATION_MENU_X_POS_OFFSET = 7;
const ROTATION_MENU_HEIGHT = 200;

const RotationMenuContextComponent: React.FC = () => {
    const { rotationMenuOpen, setRotationMenuOpen, activeStowagePlan, setStowageModalData, setIsCargoOperationPanelOpen } = useStowageStore();
    const { setSelectedTab, setSelectedViolationListFilter } = useStowageSelectionStore();
    const { setSelectedStowageRotationBerthCall, setSelectedStowageRotationPortCall, stowageRotationTree } = useRotationStore();
    const { saveStowageData } = usePostStowageApi();
    const { role } = useAuthStore();
    const [rotationItemPlaceHolder, setRotationItemPlaceHolder] = useState<RotationItemPlaceHolder>();

    const onViewViolationsInPortCall = () => {
        if (rotationMenuOpen.portCall) {
            setSelectedTab('Violation List');
            setSelectedStowageRotationBerthCall(null);
            setSelectedStowageRotationPortCall(rotationMenuOpen.portCall);
            setSelectedViolationListFilter('Selected call');
        }
        setRotationMenuOpen(null);
    };

    const onViewViolationsInBerthCall = () => {
        if (rotationMenuOpen.berthCall) {
            setSelectedTab('Violation List');
            setSelectedStowageRotationBerthCall(rotationMenuOpen.berthCall);
            setSelectedViolationListFilter('Selected call');
        }
        setRotationMenuOpen(null);
    };

    const onOpenAddPortPanel = () => {
        if (rotationMenuOpen.portCall) {
            let newPortCallOrderNumber = rotationMenuOpen.portCall.portOrderNumber + 1;
            const placeHolderIndex = stowageRotationTree.findIndex((portCall) => portCall.portCallGuid === rotationMenuOpen.portCall.portCallGuid);
            const nextPortCall = stowageRotationTree[placeHolderIndex + 1];

            if (rotationMenuOpen.portCall.turnaroundVoyageNumber) {
                const nextPortCall = stowageRotationTree[placeHolderIndex + 1];
                newPortCallOrderNumber = nextPortCall.portOrderNumber - 1;
            }

            const rotationItemPlaceHolder: RotationItemPlaceHolder = {
                type: 'add',
                level: 'port',
                placeHolderPortCallIndex: placeHolderIndex,
                newPortCallOrderNumber: newPortCallOrderNumber,
                previousPortCall: rotationMenuOpen.portCall,
                selectedPortCall: rotationMenuOpen.portCall,
                nextPortCall: nextPortCall,
            };
            setRotationItemPlaceHolder({ ...rotationItemPlaceHolder });
        }
        setRotationMenuOpen(null);
    };

    const onOpenAddBerthPanel = () => {
        const rotationItemPlaceHolder: RotationItemPlaceHolder = {
            type: 'add',
            level: 'berth',
            parentLevel: rotationMenuOpen.berthCall ? 'berth' : 'port',
            previousPortCall: null,
            selectedPortCall: rotationMenuOpen.portCall || rotationMenuOpen.berthCall?.portCall,
            selectedBerthCall: rotationMenuOpen.berthCall || null,
            nextPortCall: null,
        };
        setRotationItemPlaceHolder({ ...rotationItemPlaceHolder });
    };

    const onOpenNewCargoOperation = () => {
        setIsCargoOperationPanelOpen({ cargoOperation: null, berthCall: rotationMenuOpen.berthCall, visible: true });
    };

    const onOpenEditCallPanel = () => {
        const createEditItem = (): RotationItemPlaceHolder => {
            if (rotationMenuOpen.berthCall) {
                return {
                    type: 'edit',
                    level: 'berth',
                    previousPortCall: null,
                    selectedPortCall: rotationMenuOpen.berthCall.portCall,
                    selectedBerthCall: rotationMenuOpen.berthCall,
                    nextPortCall: null,
                };
            }

            const portCallIndex = stowageRotationTree.findIndex((portCall) => portCall.portCallGuid === rotationMenuOpen.portCall.portCallGuid);

            return {
                type: 'edit',
                level: 'port',
                placeHolderPortCallIndex: portCallIndex,
                previousPortCall: stowageRotationTree[portCallIndex - 1] || null,
                selectedPortCall: rotationMenuOpen.portCall,
                selectedBerthCall: null,
                nextPortCall: stowageRotationTree[portCallIndex + 1] || null,
            };
        };

        if (rotationMenuOpen.berthCall || rotationMenuOpen.portCall) {
            setRotationItemPlaceHolder(createEditItem());
            setRotationMenuOpen(null);
        }
    };

    const onDeleteCall = () => {
        setStowageModalData({
            title: 'Delete call',
            message: 'Are you sure you want to delete this call?',
            action: deleteCall,
            cancel: () => setStowageModalData(null),
            actionButtonText: 'Delete',
        });
    };

    const deleteCall = async () => {
        const stowagePlanChanges = [];
        const addChanges = (guid, type) => stowagePlanChanges.push(createDeleteStowagePlanChange(activeStowagePlan.stowagePlanGuid, guid, type));

        if (rotationMenuOpen.berthCall) {
            addChanges(rotationMenuOpen.berthCall.berthCallGuid, StowageChangeDataType.RotationBerthCall);
            rotationMenuOpen.berthCall.cargoOperations.forEach((cargoOperation) =>
                addChanges(cargoOperation.cargoOperationGuid, StowageChangeDataType.RotationCargoOperation)
            );
        } else if (rotationMenuOpen.portCall) {
            addChanges(rotationMenuOpen.portCall.portCallGuid, StowageChangeDataType.RotationPortCall);
            rotationMenuOpen.portCall.berthCalls.forEach((berthCall) => {
                addChanges(berthCall.berthCallGuid, StowageChangeDataType.RotationBerthCall);
                berthCall.cargoOperations.forEach((cargoOperation) =>
                    addChanges(cargoOperation.cargoOperationGuid, StowageChangeDataType.RotationCargoOperation)
                );
            });
        }
        await saveStowageData(activeStowagePlan, stowagePlanChanges);
        setRotationMenuOpen(null);
        setStowageModalData(null);
    };
    const canViewViolations = hasPermission(role, stowageFeatureEnum.ViewStowagePlan);
    const canAddPort = hasPermission(role, stowageFeatureEnum.EditRotation);

    // Check if the port call is valid for adding berth calls
    const canAddBerth = (() => {
        const hasPermissionToEdit = hasPermission(role, stowageFeatureEnum.EditRotation);
        if (!hasPermissionToEdit) return false;

        const targetPortCall = rotationMenuOpen?.berthCall?.portCall || rotationMenuOpen?.portCall;
        return targetPortCall ? isValidPortCall(targetPortCall) : false;
    })();

    // Check if the port call is valid for adding cargo operations
    const canAddCargoOperation = (() => {
        const hasPermissionToEdit = hasPermission(role, stowageFeatureEnum.EditRotation);
        if (!hasPermissionToEdit) return false;

        const targetPortCall = rotationMenuOpen?.berthCall?.portCall || rotationMenuOpen?.portCall;
        return targetPortCall ? isValidPortCall(targetPortCall) : false;
    })();

    const canEditCall = hasPermission(role, stowageFeatureEnum.EditRotation);
    const canDeleteCall = () => {
        let _hasBerthsOrCargoOperations = false;
        let _hasBlockedTanks = false;

        if (rotationMenuOpen && rotationMenuOpen.berthCall) {
            _hasBerthsOrCargoOperations = rotationMenuOpen.berthCall.cargoOperations.length > 0;
            _hasBlockedTanks = hasRotationItemBlockedTank(activeStowagePlan, rotationMenuOpen.portCall, rotationMenuOpen.berthCall);
        } else if (rotationMenuOpen && !rotationMenuOpen.berthCall && rotationMenuOpen.portCall) {
            _hasBerthsOrCargoOperations = getBerthCalls(rotationMenuOpen.portCall).length > 0;
            _hasBlockedTanks = hasRotationItemBlockedTank(activeStowagePlan, rotationMenuOpen.portCall, null);
        }

        return hasPermission(role, stowageFeatureEnum.EditRotation) && !_hasBerthsOrCargoOperations && !_hasBlockedTanks;
    };

    const menuItems = [
        { content: 'Add port call', isDisabled: !canAddPort, onClick: onOpenAddPortPanel, scope: ['port'] },
        { content: 'Add berth call', isDisabled: !canAddBerth, onClick: onOpenAddBerthPanel, scope: ['port', 'berth'] },
        { content: 'Add cargo operation', isDisabled: !canAddCargoOperation, onClick: onOpenNewCargoOperation, scope: ['berth'] },
        { content: 'Edit call', isDisabled: !canEditCall, onClick: onOpenEditCallPanel, scope: ['port', 'berth'] },
        { content: 'Delete call', isDisabled: !canDeleteCall(), onClick: onDeleteCall, scope: ['port', 'berth'] },
        { isSeparator: true, scope: ['port', 'berth'] },
        { content: 'View violations', isDisabled: !canViewViolations, onClick: onViewViolationsInPortCall, scope: ['port'] },
        { content: 'View violations', isDisabled: !canViewViolations, onClick: onViewViolationsInBerthCall, scope: ['berth'] },
    ];
    const createMenuItems = (scope: string) =>
        menuItems
            .filter((item) => item.scope.includes(scope))
            .map((item) => ({
                isSeparator: item.isSeparator ?? false,
                content: item.content,
                isDisabled: item.isDisabled,
                onClick: item.onClick,
            }));

    const getRotationMenuYPos = (rotationMenuOpen: MenuPosition) => {
        if (rotationMenuOpen.top + ROTATION_MENU_HEIGHT > window.innerHeight) {
            return rotationMenuOpen.top - ROTATION_MENU_Y_POS_OFFSET - ROTATION_MENU_HEIGHT / 2;
        }

        return rotationMenuOpen.top - ROTATION_MENU_Y_POS_OFFSET;
    };

    const onClosePanel = () => {
        setRotationItemPlaceHolder(null);
    };

    return (
        <RotationMenuWrapper>
            {rotationMenuOpen?.isVisible && (
                <RotationMenu style={{ left: ROTATION_MENU_X_POS_OFFSET, top: getRotationMenuYPos(rotationMenuOpen) }}>
                    <OdfjellContextMenu
                        position={{ x: 0, y: 0 }}
                        menuItems={createMenuItems(rotationMenuOpen.berthCall ? 'berth' : 'port')}
                        dataContextMenu="rotation"
                    />
                </RotationMenu>
            )}

            {rotationItemPlaceHolder &&
                (rotationItemPlaceHolder.level === 'port' ? (
                    <PortCallRotationPanel rotationItemPlaceHolder={rotationItemPlaceHolder} onClosePanel={onClosePanel} />
                ) : (
                    <BerthCallRotationPanel rotationItemPlaceHolder={rotationItemPlaceHolder} onClosePanel={onClosePanel} />
                ))}
        </RotationMenuWrapper>
    );
};

export default RotationMenuContextComponent;
